import React, { useEffect, useState, useContext } from 'react';
import './ListaDatabase.css';
import { fetchDocuments } from '../../../utils/documentUtils';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ReplaceModal from './ReplaceModal';
import DocumentDetailsModal from './DocumentDetailsModal';
import { AuthContext } from '../../../authContext';
import { useTranslation } from 'react-i18next';

function ListaDatabase({ classeCSS, updateFlag, documentList, setDocumentList, user, triggerUpdate, filters, assistant, assistantId, sessionId }) {
    const { accessToken } = useContext(AuthContext);
    const { t } = useTranslation('database');
    const { t: tCommon } = useTranslation('common');
    const isSiteAssist = String(assistantId) === '3';
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [isReplaceModal, setIsReplaceModal] = useState(false);
    const [docToReplace, setDocToReplace] = useState(null);
    const [reprocessingFileName, setReprocessingFileName] = useState('');
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [docForDetails, setDocForDetails] = useState(null);
    const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

    const refreshDocuments = async () => {
        setIsLoadingDocuments(true);
        try {
            await fetchDocuments(setDocumentList, assistant, accessToken);
        } finally {
            setIsLoadingDocuments(false);
        }
    };

    const handleSelectDocument = (index) => {
        setSelectedDocument(selectedDocument === index ? null : index);
    };

    useEffect(() => {
        refreshDocuments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateFlag, setDocumentList, accessToken, assistant]);

    const openDeleteModal = (docFileName) => {
        setDocToDelete(docFileName);
        setIsDeleteModalOpen(true);
    };

    const handleConfirmDelete = () => {
        deleteDocument(docToDelete);
        setIsDeleteModalOpen(false);
    };

    const openReplaceModal = (docFileName) => {
        setDocToReplace(docFileName);
        setIsReplaceModal(true);
    };

    const openDetailsModal = (doc) => {
        if (!doc) {
            return;
        }
        setDocForDetails(doc);
        setIsDetailsModalOpen(true);
    };

    const deleteDocument = (fileName) => {
        fetch(`${process.env.REACT_APP_API_URL}/delete_site`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`
            },
            body: JSON.stringify({ filename: fileName, user, assistant }),
        })
            .then(response => response.json())
            .then(data => {
                setSelectedDocument(null);
                alert(`${t('success')}: ${data.message}`);
                refreshDocuments();
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    };

    const reprocessDocument = async (fileName) => {
        if (!isSiteAssist || !fileName || reprocessingFileName) {
            return;
        }
        setReprocessingFileName(fileName);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/post_reprocess_site_documents`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${accessToken}`
                },
                body: JSON.stringify({ session_id: sessionId, file_names: [fileName] }),
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || t('reprocess_document_error'));
            }
            triggerUpdate();
            setTimeout(() => triggerUpdate(), 4000);
            alert(t('reprocess_started_single'));
        } catch (error) {
            console.error('Error reprocessing document:', error);
            alert(error.message || t('reprocess_document_error'));
        } finally {
            setReprocessingFileName('');
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${day}/${month}/${year}`;
    };

    const formatMonth = (dateString) => {
        const date = new Date(dateString);
        const month = String(date.getUTCMonth() + 1).padStart(2, '0');
        const year = date.getUTCFullYear();
        return `${month}/${year}`;
    };

    const getProcessingLabel = (doc) => {
        const metrics = doc.state_metrics;
        if (!metrics || !metrics.extraction_status) {
            return t('no_metrics');
        }
        const status = String(metrics.extraction_status || '').toLowerCase();
        const labelMap = {
            queued: t('processing_queued'),
            running: t('processing_running'),
            completed: t('processing_completed'),
            failed: t('processing_failed'),
            invalidated: t('processing_invalidated'),
        };
        return labelMap[status] || metrics.extraction_status;
    };

    const getProcessingMeta = (doc) => {
        const status = String(doc?.state_metrics?.extraction_status || '').toLowerCase();
        const label = getProcessingLabel(doc);
        const mapping = {
            queued: { className: 'queued', icon: '/imagens/ArrowsClockwise.svg' },
            running: { className: 'running', icon: '/imagens/ArrowsClockwise.svg' },
            completed: { className: 'completed', icon: '/imagens/check.svg' },
            failed: { className: 'failed', icon: '/imagens/x-circle.svg' },
            invalidated: { className: 'invalidated', icon: '/imagens/x-circle.svg' },
            'no-metrics': { className: 'no-metrics', icon: '/imagens/alert-circle.svg' },
        };
        const fallback = status && mapping[status] ? mapping[status] : mapping['no-metrics'];
        return {
            label,
            className: fallback.className,
            icon: fallback.icon,
        };
    };

    const getDocumentStatusLabel = (status) => {
        if (status === 'Official') {
            return t('official');
        }
        if (status === 'Draft') {
            return t('draft');
        }
        return status;
    };

    const filteredDocuments = documentList.filter((doc) => {
        const matchesStatus = (filters.selectedStatus === 'Todos' || filters.selectedStatus === '')
            ? doc.Status === 'Official' || doc.Status === 'Draft'
            : doc.Status === filters.selectedStatus;

        const docMonthYear = formatMonth(doc.Date);
        const matchesDate = filters.selectedDate === '' || docMonthYear === filters.selectedDate;
        const matchesName = filters.searchTerm === '' || doc.FileName.toLowerCase().includes(filters.searchTerm.toLowerCase());

        return matchesStatus && matchesDate && matchesName;
    });

    return (
        <div className={classeCSS}>
            <ConfirmDeleteModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
            />
            <ReplaceModal
                isOpen={isReplaceModal}
                onClose={() => setIsReplaceModal(false)}
                docToReplace={docToReplace}
                user={user}
                setDocumentList={setDocumentList}
                documentList={documentList}
                setSelectedDocument={setSelectedDocument}
                triggerUpdate={triggerUpdate}
                assistant={assistant}
                sessionId={sessionId}
            />
            <DocumentDetailsModal
                isOpen={isDetailsModalOpen}
                onClose={() => setIsDetailsModalOpen(false)}
                documentItem={docForDetails}
                accessToken={accessToken}
            />
            <div className="document-list">
                {isLoadingDocuments && (
                    <div className="documents-loading-state">
                        <div className="documents-loading-spinner" />
                        <span>{tCommon('loading')}</span>
                    </div>
                )}
                {filteredDocuments.map((doc, index) => {
                    const processingMeta = getProcessingMeta(doc);
                    const isReprocessingThisDoc = reprocessingFileName === doc.FileName;
                    return (
                        <div key={doc.FileName || index} className={`document-item ${selectedDocument === index ? 'selected' : ''}`}>
                            <div className="document-checkbox" onClick={() => handleSelectDocument(index)}>
                                <img
                                    src={selectedDocument === index ? '/imagens/marked-checkbox.svg' : '/imagens/checkbox.svg'}
                                    alt={selectedDocument === index ? t('checked') : t('unchecked')}
                                    className="checkbox-icon"
                                />
                            </div>

                            <div className="document-content">
                                {selectedDocument === index && (
                                    <div className="document-actions">
                                        <img
                                            src="/imagens/trash.svg"
                                            alt={t('delete')}
                                            className="action-icon"
                                            onClick={() => openDeleteModal(doc.FileName)}
                                        />
                                        <img
                                            src="/imagens/replace.svg"
                                            alt={t('replace_file')}
                                            className="action-icon"
                                            onClick={() => openReplaceModal(doc.FileName)}
                                        />
                                        {isSiteAssist && (
                                            <button
                                                type="button"
                                                className="reprocess-doc-button"
                                                onClick={() => reprocessDocument(doc.FileName)}
                                                disabled={Boolean(reprocessingFileName)}
                                            >
                                                {isReprocessingThisDoc ? t('reprocessing') : t('reprocess')}
                                            </button>
                                        )}
                                    </div>
                                )}

                                <div className={`document-name ${selectedDocument === index ? 'name-shrink' : ''}`}>
                                    <button
                                        type="button"
                                        className="document-open-details-button"
                                        onClick={() => openDetailsModal(doc)}
                                        title={t('document_details_open')}
                                    >
                                        {doc.FileName}
                                    </button>
                                </div>

                                <div className="document-status">
                                    {getDocumentStatusLabel(doc.Status)}
                                </div>

                                <div className="document-date">
                                    {formatDate(doc.Date)}
                                </div>
                            </div>

                            {isSiteAssist && (
                                <div className="document-processing-column">
                                    <span
                                        className={`processing-status-icon ${processingMeta.className}`}
                                        title={processingMeta.label}
                                        aria-label={processingMeta.label}
                                    >
                                        <img
                                            src={processingMeta.icon}
                                            alt={processingMeta.label}
                                            className="processing-status-icon-image"
                                        />
                                    </span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default ListaDatabase;


